import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  CreditCard, 
  Plus, 
  Search, 
  Calendar, 
  DollarSign, 
  AlertCircle,
  CheckCircle,
  X,
  Edit,
  Trash2,
  ExternalLink,
  TrendingUp,
  Clock,
  Users,
  ChevronDown,
  ChevronUp,
  Globe,
  Tag,
  CreditCard as CreditCardIcon,
  Calendar as CalendarIcon,
  DollarSign as DollarSignIcon,
  FileText
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { Card } from '../common/Card';
import { Button } from '../common/Button';
import { Modal } from '../common/Modal';
import { SearchInput } from '../common/SearchInput';

interface Subscription {
  id: string;
  name: string;
  logo: string;
  category: string;
  monthly_cost: number;
  billing_cycle: 'monthly' | 'yearly' | 'quarterly';
  next_billing: string;
  status: 'active' | 'cancelled' | 'paused';
  website: string;
  notes?: string;
  auto_renew: boolean;
  shared_with?: string[];
  created_at: string;
}

interface RecommendedService {
  id: string;
  name: string;
  logo: string;
  category: string;
  monthly_cost: number;
  description: string;
  website: string;
  popularity: number;
}

const recommendedServices: RecommendedService[] = [
  {
    id: 'netflix',
    name: 'Netflix',
    logo: 'https://upload.wikimedia.org/wikipedia/commons/0/08/Netflix_2015_logo.svg',
    category: 'Entertainment',
    monthly_cost: 15.99,
    description: 'Streaming movies and TV shows',
    website: 'https://netflix.com',
    popularity: 95
  },
  {
    id: 'spotify',
    name: 'Spotify',
    logo: 'https://upload.wikimedia.org/wikipedia/commons/1/19/Spotify_logo_without_text.svg',
    category: 'Music',
    monthly_cost: 9.99,
    description: 'Music streaming service',
    website: 'https://spotify.com',
    popularity: 90
  },
  {
    id: 'amazon-prime',
    name: 'Amazon Prime',
    logo: 'https://upload.wikimedia.org/wikipedia/commons/2/27/Amazon_Prime_logo.png',
    category: 'Shopping',
    monthly_cost: 12.99,
    description: 'Fast shipping and streaming',
    website: 'https://amazon.com/prime',
    popularity: 88
  },
  {
    id: 'disney-plus',
    name: 'Disney+',
    logo: 'https://upload.wikimedia.org/wikipedia/commons/3/3e/Disney%2B_logo.svg',
    category: 'Entertainment',
    monthly_cost: 7.99,
    description: 'Disney, Marvel, and Star Wars content',
    website: 'https://disneyplus.com',
    popularity: 85
  },
  {
    id: 'youtube-premium',
    name: 'YouTube Premium',
    logo: 'https://upload.wikimedia.org/wikipedia/commons/0/09/YouTube_full-color_icon_%282017%29.svg',
    category: 'Entertainment',
    monthly_cost: 11.99,
    description: 'Ad-free YouTube and music',
    website: 'https://youtube.com/premium',
    popularity: 82
  },
  {
    id: 'apple-one',
    name: 'Apple One',
    logo: 'https://upload.wikimedia.org/wikipedia/commons/f/fa/Apple_logo_black.svg',
    category: 'Technology',
    monthly_cost: 16.95,
    description: 'Apple services bundle',
    website: 'https://apple.com/apple-one',
    popularity: 80
  },
  {
    id: 'microsoft-365',
    name: 'Microsoft 365',
    logo: 'https://upload.wikimedia.org/wikipedia/commons/9/96/Microsoft_logo_%282012%29.svg',
    category: 'Productivity',
    monthly_cost: 6.99,
    description: 'Office apps and cloud storage',
    website: 'https://microsoft365.com',
    popularity: 78
  },
  {
    id: 'adobe-creative-cloud',
    name: 'Adobe Creative Cloud',
    logo: 'https://upload.wikimedia.org/wikipedia/commons/7/7b/Adobe_Systems_logo_and_wordmark.svg',
    category: 'Creative',
    monthly_cost: 52.99,
    description: 'Creative software suite',
    website: 'https://adobe.com/creativecloud',
    popularity: 75
  },
  {
    id: 'dropbox',
    name: 'Dropbox',
    logo: 'https://upload.wikimedia.org/wikipedia/commons/7/7e/Dropbox_Icon.svg',
    category: 'Storage',
    monthly_cost: 9.99,
    description: 'Cloud storage and file sharing',
    website: 'https://dropbox.com',
    popularity: 72
  },
  {
    id: 'zoom',
    name: 'Zoom Pro',
    logo: 'https://upload.wikimedia.org/wikipedia/commons/9/9e/Zoom_logo.svg',
    category: 'Communication',
    monthly_cost: 14.99,
    description: 'Video conferencing platform',
    website: 'https://zoom.us',
    popularity: 70
  }
];

const categories = [
  'Entertainment',
  'Music',
  'Shopping',
  'Technology',
  'Productivity',
  'Creative',
  'Storage',
  'Communication',
  'Fitness',
  'Education',
  'Other'
];

export function Subscriptions() {
  const { user } = useAuth();
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showRecommendations, setShowRecommendations] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [expandedSubscriptions, setExpandedSubscriptions] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newSubscription, setNewSubscription] = useState({
    name: '',
    logo: '',
    category: '',
    monthly_cost: 0,
    billing_cycle: 'monthly' as const,
    next_billing: '',
    status: 'active' as const,
    website: '',
    notes: '',
    auto_renew: true,
  });

  useEffect(() => {
    if (user) {
      loadSubscriptions();
    }
  }, [user]);

  useEffect(() => {
    const handler = () => { if (user) loadSubscriptions(true); };
    window.addEventListener('refresh-digital', handler);
    return () => window.removeEventListener('refresh-digital', handler);
  }, [user]);

  async function loadSubscriptions(background = false) {
    try {
      if (!background) setLoading(true);
      const { data, error } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('user_id', user?.id)
        .order('next_billing', { ascending: true });

      if (error) throw error;
      setSubscriptions(data || []);
    } catch (err) {
      if (!background) setError(err instanceof Error ? err.message : 'Failed to load subscriptions');
    } finally {
      if (!background) setLoading(false);
    }
  }

  const addSubscription = async (subscription: Omit<Subscription, 'id' | 'created_at'>) => {
    try {
      if (!user) {
        setError('User not authenticated');
        return;
      }

      if (!subscription.name || subscription.monthly_cost <= 0) {
        setError('Service name and monthly cost are required');
        return;
      }

      const { data, error } = await supabase
        .from('subscriptions')
        .insert([{
          ...subscription,
          user_id: user.id,
          created_at: new Date().toISOString()
        }])
        .select()
        .single();

      if (error) throw error;
      setSubscriptions(prev => [...prev, data]);
      setShowAddModal(false);
      setNewSubscription({
        name: '',
        logo: '',
        category: '',
        monthly_cost: 0,
        billing_cycle: 'monthly',
        next_billing: '',
        status: 'active',
        website: '',
        notes: '',
        auto_renew: true,
      });
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add subscription');
    }
  };

  const addRecommendedService = (service: RecommendedService) => {
    setNewSubscription({
      name: service.name,
      logo: service.logo,
      category: service.category,
      monthly_cost: service.monthly_cost,
      billing_cycle: 'monthly',
      next_billing: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      status: 'active',
      website: service.website,
      notes: service.description,
      auto_renew: true,
    });
    setShowRecommendations(false);
    setShowAddModal(true);
  };

  const deleteSubscription = async (id: string) => {
    try {
      const { error } = await supabase
        .from('subscriptions')
        .delete()
        .eq('id', id);

      if (error) throw error;
      setSubscriptions(prev => prev.filter(sub => sub.id !== id));
      setExpandedSubscriptions(prev => {
        const newState = { ...prev };
        delete newState[id];
        return newState;
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete subscription');
    }
  };

  const toggleExpandedSubscription = (id: string) => {
    setExpandedSubscriptions(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const filteredSubscriptions = subscriptions.filter(subscription => {
    const matchesSearch = 
      subscription.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      subscription.category.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesCategory = !selectedCategory || subscription.category === selectedCategory;
    
    return matchesSearch && matchesCategory;
  });

  const totalMonthlyCost = subscriptions
    .filter(sub => sub.status === 'active')
    .reduce((sum, sub) => sum + sub.monthly_cost, 0);

  const upcomingBills = subscriptions
    .filter(sub => sub.status === 'active')
    .sort((a, b) => new Date(a.next_billing).getTime() - new Date(b.next_billing).getTime())
    .slice(0, 5);

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Subscription Manager</h1>
          <p className="text-gray-600 mt-1">Track and manage your digital subscriptions</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setShowRecommendations(true)}
            icon={<TrendingUp size={20} />}
          >
            Popular Services
          </Button>
          <Button
            variant="primary"
            onClick={() => setShowAddModal(true)}
            icon={<Plus size={20} />}
          >
            Add Subscription
          </Button>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-4 bg-red-50 text-red-700 rounded-lg">
          <AlertCircle size={20} />
          <p>{error}</p>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <div className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Active Subscriptions</p>
                <p className="text-2xl font-bold text-gray-900">
                  {subscriptions.filter(sub => sub.status === 'active').length}
                </p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <CreditCard size={24} className="text-blue-600" />
              </div>
            </div>
          </div>
        </Card>
        <Card>
          <div className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Monthly Cost</p>
                <p className="text-2xl font-bold text-gray-900">
                  ${totalMonthlyCost.toFixed(2)}
                </p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <DollarSign size={24} className="text-green-600" />
              </div>
            </div>
          </div>
        </Card>
        <Card>
          <div className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Next Bill</p>
                <p className="text-2xl font-bold text-gray-900">
                  {upcomingBills.length > 0 ? 
                    new Date(upcomingBills[0].next_billing).toLocaleDateString() : 
                    'None'
                  }
                </p>
              </div>
              <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
                <Calendar size={24} className="text-amber-600" />
              </div>
            </div>
          </div>
        </Card>
      </div>

      <div className="flex gap-4">
        <div className="w-64 space-y-6">
          <Card>
            <div className="p-4">
              <h2 className="font-semibold text-gray-900 mb-4">Categories</h2>
              <div className="space-y-2">
                <button
                  onClick={() => setSelectedCategory(null)}
                  className={`w-full text-left px-3 py-2 rounded-lg transition-colors
                    ${!selectedCategory ? 'bg-idecide-primary text-idecide-accent' : 'hover:bg-gray-100'}`}
                >
                  All Subscriptions
                </button>
                {categories.map(category => (
                  <button
                    key={category}
                    onClick={() => setSelectedCategory(category)}
                    className={`w-full text-left px-3 py-2 rounded-lg transition-colors
                      ${selectedCategory === category ? 'bg-idecide-primary text-idecide-accent' : 'hover:bg-gray-100'}`}
                  >
                    {category}
                  </button>
                ))}
              </div>
            </div>
          </Card>

          <Card>
            <div className="p-4">
              <h2 className="font-semibold text-gray-900 mb-4">Upcoming Bills</h2>
              <div className="space-y-3">
                {upcomingBills.map(subscription => (
                  <div key={subscription.id} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <img 
                        src={subscription.logo} 
                        alt={subscription.name}
                        className="w-6 h-6 rounded"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                      <span className="text-sm font-medium">{subscription.name}</span>
                    </div>
                    <span className="text-sm text-gray-600">
                      ${subscription.monthly_cost}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </Card>
        </div>

        <div className="flex-1 space-y-6">
          <SearchInput
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search subscriptions..."
            className="w-full"
          />

          <Card>
            <div className="p-6">
              <div className="space-y-4">
                {filteredSubscriptions.length > 0 ? (
                  filteredSubscriptions.map(subscription => (
                    <motion.div
                      key={subscription.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="border border-gray-200 rounded-lg overflow-hidden"
                    >
                      {/* Main Subscription Row */}
                      <div className="flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors">
                        <div className="flex items-center gap-4">
                          <div className="p-2 bg-white rounded-lg">
                            <CreditCardIcon className="text-gray-600" size={20} />
                          </div>
                          <div>
                            <h3 className="font-medium text-gray-900">{subscription.name}</h3>
                            <p className="text-sm text-gray-500">{subscription.category}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {/* Status Badge */}
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            subscription.status === 'active' ? 'bg-green-100 text-green-800' :
                            subscription.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                            'bg-yellow-100 text-yellow-800'
                          }`}>
                            {subscription.status}
                          </span>
                          
                          {/* Expand/Collapse Button */}
                          <button
                            onClick={() => toggleExpandedSubscription(subscription.id)}
                            className="p-2 text-gray-500 hover:text-gray-700"
                            title={expandedSubscriptions[subscription.id] ? 'Hide details' : 'Show details'}
                          >
                            {expandedSubscriptions[subscription.id] ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                          </button>
                          
                          {/* Delete Button */}
                          <button
                            onClick={() => deleteSubscription(subscription.id)}
                            className="p-2 text-red-500 hover:text-red-700"
                            title="Delete subscription"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>

                      {/* Expanded Details Panel */}
                      {expandedSubscriptions[subscription.id] && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          className="bg-white border-t border-gray-200"
                        >
                          <div className="p-6 space-y-4">
                            {/* Service Information */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              {/* Service Name */}
                              <div className="space-y-2">
                                <label className="text-sm font-medium text-gray-700">Service Name</label>
                                <div className="p-2 bg-gray-50 border border-gray-200 rounded-lg">
                                  <span className="text-sm text-gray-600">{subscription.name}</span>
                                </div>
                              </div>

                              {/* Category */}
                              <div className="space-y-2">
                                <label className="text-sm font-medium text-gray-700">Category</label>
                                <div className="p-2 bg-gray-50 border border-gray-200 rounded-lg">
                                  <span className="text-sm text-gray-600">{subscription.category}</span>
                                </div>
                              </div>
                            </div>

                            {/* Billing Information */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                              {/* Monthly Cost */}
                              <div className="space-y-2">
                                <label className="text-sm font-medium text-gray-700">Monthly Cost</label>
                                <div className="p-2 bg-gray-50 border border-gray-200 rounded-lg">
                                  <span className="text-sm text-gray-600">${subscription.monthly_cost}</span>
                                </div>
                              </div>

                              {/* Billing Cycle */}
                              <div className="space-y-2">
                                <label className="text-sm font-medium text-gray-700">Billing Cycle</label>
                                <div className="p-2 bg-gray-50 border border-gray-200 rounded-lg">
                                  <span className="text-sm text-gray-600 capitalize">{subscription.billing_cycle}</span>
                                </div>
                              </div>

                              {/* Next Billing */}
                              <div className="space-y-2">
                                <label className="text-sm font-medium text-gray-700">Next Billing</label>
                                <div className="p-2 bg-gray-50 border border-gray-200 rounded-lg">
                                  <span className="text-sm text-gray-600">
                                    {new Date(subscription.next_billing).toLocaleDateString()}
                                  </span>
                                </div>
                              </div>
                            </div>

                            {/* Additional Information */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              {/* Website */}
                              {subscription.website && (
                                <div className="space-y-2">
                                  <label className="text-sm font-medium text-gray-700">Website</label>
                                  <div className="flex items-center gap-2">
                                    <input
                                      type="url"
                                      value={subscription.website}
                                      readOnly
                                      className="flex-1 p-2 bg-gray-50 border border-gray-200 rounded-lg text-sm"
                                    />
                                    <a
                                      href={subscription.website}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="p-2 text-gray-500 hover:text-gray-700"
                                      title="Open website"
                                    >
                                      <ExternalLink size={16} />
                                    </a>
                                  </div>
                                </div>
                              )}

                              {/* Auto Renew */}
                              <div className="space-y-2">
                                <label className="text-sm font-medium text-gray-700">Auto Renew</label>
                                <div className="p-2 bg-gray-50 border border-gray-200 rounded-lg">
                                  <span className="text-sm text-gray-600">
                                    {subscription.auto_renew ? 'Yes' : 'No'}
                                  </span>
                                </div>
                              </div>
                            </div>

                            {/* Notes */}
                            {subscription.notes && (
                              <div className="space-y-2">
                                <label className="text-sm font-medium text-gray-700">Notes</label>
                                <textarea
                                  value={subscription.notes}
                                  readOnly
                                  className="w-full p-2 bg-gray-50 border border-gray-200 rounded-lg text-sm"
                                  rows={3}
                                />
                              </div>
                            )}

                            {/* Created Date */}
                            <div className="text-sm text-gray-500">
                              Added: {new Date(subscription.created_at).toLocaleDateString()}
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </motion.div>
                  ))
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    {searchQuery 
                      ? 'No subscriptions match your search' 
                      : 'No subscriptions found. Add your first subscription to get started.'}
                  </div>
                )}
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Add Subscription Modal */}
      <Modal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        title="Add Subscription"
      >
        <div className="space-y-6">
          {/* 1. Service Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Service Name
            </label>
            <input
              type="text"
              value={newSubscription.name}
              onChange={(e) => setNewSubscription({ ...newSubscription, name: e.target.value })}
              className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-idecide-accent"
              placeholder="e.g., Netflix, Spotify, Amazon Prime"
            />
          </div>

          {/* 2. Website URL */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Website URL
            </label>
            <input
              type="url"
              value={newSubscription.website}
              onChange={(e) => setNewSubscription({ ...newSubscription, website: e.target.value })}
              className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-idecide-accent"
              placeholder="https://example.com"
            />
          </div>

          {/* 3. Category (Dropdown) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Category
            </label>
            <select
              value={newSubscription.category}
              onChange={(e) => setNewSubscription({ ...newSubscription, category: e.target.value })}
              className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-idecide-accent"
            >
              <option value="">Select category</option>
              {categories.map(category => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>
          </div>

          {/* 4. Monthly Cost */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Monthly Cost
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={newSubscription.monthly_cost}
              onChange={(e) => setNewSubscription({ ...newSubscription, monthly_cost: parseFloat(e.target.value) || 0 })}
              className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-idecide-accent"
              placeholder="9.99"
            />
          </div>

          {/* 5. Billing Cycle */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Billing Cycle
            </label>
            <select
              value={newSubscription.billing_cycle}
              onChange={(e) => setNewSubscription({ ...newSubscription, billing_cycle: e.target.value as any })}
              className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-idecide-accent"
            >
              <option value="monthly">Monthly</option>
              <option value="yearly">Yearly</option>
              <option value="quarterly">Quarterly</option>
            </select>
          </div>

          {/* 6. Next Billing Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Next Billing Date
            </label>
            <input
              type="date"
              value={newSubscription.next_billing}
              onChange={(e) => setNewSubscription({ ...newSubscription, next_billing: e.target.value })}
              className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-idecide-accent"
            />
          </div>

          {/* 7. Auto Renew */}
          <div>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={newSubscription.auto_renew}
                onChange={(e) => setNewSubscription({ ...newSubscription, auto_renew: e.target.checked })}
                className="rounded border-gray-300 text-idecide-accent focus:ring-idecide-accent"
              />
              <span className="text-sm font-medium text-gray-700">Auto Renew</span>
            </label>
          </div>

          {/* 8. Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes
            </label>
            <textarea
              value={newSubscription.notes}
              onChange={(e) => setNewSubscription({ ...newSubscription, notes: e.target.value })}
              className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-idecide-accent"
              rows={3}
              placeholder="Additional notes about this subscription..."
            />
          </div>

          {/* Save or Discard */}
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setShowAddModal(false)}
            >
              Discard
            </Button>
            <Button
              variant="primary"
              onClick={() => addSubscription(newSubscription)}
            >
              Save Subscription
            </Button>
          </div>
        </div>
      </Modal>

      {/* Popular Services Modal */}
      <Modal
        isOpen={showRecommendations}
        onClose={() => setShowRecommendations(false)}
        title="Popular Subscription Services"
      >
        <div className="space-y-4">
          <p className="text-gray-600">Add one of these popular services to your subscription manager:</p>
          <div className="grid grid-cols-1 gap-3 max-h-96 overflow-y-auto">
            {recommendedServices.map(service => (
              <motion.div
                key={service.id}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Card className="cursor-pointer" onClick={() => addRecommendedService(service)}>
                  <div className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <img 
                          src={service.logo} 
                          alt={service.name}
                          className="w-10 h-10 rounded-lg object-cover"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                        <div>
                          <h3 className="font-semibold text-gray-900">{service.name}</h3>
                          <p className="text-sm text-gray-600">{service.description}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-sm font-medium text-gray-900">
                              ${service.monthly_cost}/month
                            </span>
                            <span className="text-xs text-gray-500">
                              {service.category}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex items-center gap-1 text-xs text-gray-500">
                          <Users size={14} />
                          <span>{service.popularity}%</span>
                        </div>
                        <Plus size={20} className="text-gray-400" />
                      </div>
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </Modal>
    </div>
  );
} 